// 画像変換オプションの型定義
interface ImageOptimizationOptions {
  width?: number
  height?: number
  quality?: number
  format?: 'webp' | 'jpg' | 'png'
}

interface ImageSource {
  src?: string
  url?: string
  width?: number
  height?: number
  alt?: string
}

/**
 * MicroCMS画像URLの最適化パラメータを追加
 * @param url 元の画像URL
 * @param options 最適化オプション
 * @returns 最適化された画像URL
 */
export function getOptimizedImageUrl(
  url: string,
  options: ImageOptimizationOptions = {}
): string {
  if (!url) return ''
  // public/ 配下の相対パス（Storybook のサンプル画像など）は microCMS の変換対象ではない
  if (url.startsWith('/') && !url.startsWith('//')) return url

  try {
    const urlObj = new URL(url)

    // MicroCMSの画像変換パラメータを設定
    if (options.format) {
      urlObj.searchParams.set('fm', options.format)
    } else {
      urlObj.searchParams.set('fm', 'webp')
    }

    if (options.quality !== undefined) {
      const quality = Math.min(100, Math.max(1, options.quality))
      urlObj.searchParams.set('q', quality.toString())
    }

    if (options.width) {
      urlObj.searchParams.set('w', Math.floor(options.width).toString())
    }

    if (options.height) {
      urlObj.searchParams.set('h', Math.floor(options.height).toString())
    }

    // 自動リサイズモードを有効にする
    if (options.width || options.height) {
      urlObj.searchParams.set('fit', 'crop')
    }

    return urlObj.toString()
  } catch (error) {
    console.error('Error optimizing image URL:', error)
    return url
  }
}

/**
 * WebP形式への変換（従来関数の改良版）
 */
export function toWebP(
  url: string,
  options: ImageOptimizationOptions = {}
): string {
  return getOptimizedImageUrl(url, { ...options, format: 'webp' })
}

/**
 * レスポンシブ画像用のsrcSet生成
 */
export function generateSrcSet(
  url: string,
  widths: number[] = [400, 800, 1200],
  quality: number = 85
): string {
  if (!url) return ''

  return widths
    .map((width) => {
      const optimizedUrl = getOptimizedImageUrl(url, {
        width,
        quality,
        format: 'webp'
      })
      return `${optimizedUrl} ${width}w`
    })
    .join(', ')
}

/**
 * 画像コンポーネント用のprops生成（改良版）
 */
export function getOptimizedImageProps(
  image: ImageSource,
  options: ImageOptimizationOptions & {
    className?: string
    sizes?: string
    priority?: boolean
  } = {}
) {
  const imageUrl = image.src || image.url || ''
  if (!imageUrl) {
    return {
      src: '',
      alt: image.alt || '',
      className: options.className || ''
    }
  }

  const optimizedUrl = getOptimizedImageUrl(imageUrl, options)
  const srcSet = options.width
    ? generateSrcSet(
        imageUrl,
        [options.width * 0.5, options.width, options.width * 1.5].map(
          Math.floor
        )
      )
    : generateSrcSet(imageUrl)

  return {
    src: optimizedUrl,
    srcSet,
    sizes:
      options.sizes ||
      '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    width: options.width || image.width || 800,
    height: options.height || image.height || 600,
    alt: image.alt || '',
    className: options.className || '',
    loading: options.priority ? ('eager' as const) : ('lazy' as const),
    ...(options.priority && { priority: true })
  }
}

/**
 * MicroCMS用リッチテキスト内画像最適化関数（改良版）
 */
export function optimizeImagesInHtml(
  htmlContent: string,
  options: ImageOptimizationOptions = {}
): string {
  if (!htmlContent) return ''

  return htmlContent.replace(
    /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
    (_match, beforeSrc, src, afterSrc) => {
      // MicroCMSの画像URLかチェック
      if (src.includes('microcms-assets.io')) {
        const optimizedSrc = getOptimizedImageUrl(src, {
          quality: options.quality || 80,
          format: 'webp',
          ...options
        })

        // loading="lazy"が既にあるかチェック
        const hasLoading = /loading\s*=/.test(beforeSrc + afterSrc)
        const loadingAttr = hasLoading ? '' : ' loading="lazy"'

        // decoding="async"が既にあるかチェック
        const hasDecoding = /decoding\s*=/.test(beforeSrc + afterSrc)
        const decodingAttr = hasDecoding ? '' : ' decoding="async"'

        return `<img${beforeSrc} src="${optimizedSrc}"${afterSrc}${loadingAttr}${decodingAttr}>`
      }

      // MicroCMS以外の画像はそのままだが、loading="lazy"は追加
      const hasLoading = /loading\s*=/.test(beforeSrc + afterSrc)
      const loadingAttr = hasLoading ? '' : ' loading="lazy"'

      return `<img${beforeSrc} src="${src}"${afterSrc}${loadingAttr}>`
    }
  )
}
